import { useParams, Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import {
  ArrowLeft,
  User,
  Mail,
  Calendar,
  Building2,
  Monitor,
  Ban,
  Pencil,
} from "lucide-react";
import { OwnerSubscriptions } from "@/admin/components/OwnerSubscriptions";

export function AdminUserPage() {
  const { userId } = useParams<{ userId: string }>();

  const {
    data: userData,
    isLoading,
    error,
  } = trpc.admin.getUser.useQuery(
    { userId: userId! },
    {
      enabled: !!userId,
    }
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">Error loading user: {error.message}</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">User not found</div>
      </div>
    );
  }

  const { user, memberships, sessionCount, emailPreferences } = userData;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/users">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Users
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <User className="h-6 w-6 text-violet-pulse" />
          <h1 className="text-2xl font-bold text-silica font-display">User: {user.name || user.email}</h1>
          {user.banned && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <Ban className="h-3 w-3" />
              Banned
            </Badge>
          )}
          {user.role === "admin" && (
            <Badge className="bg-violet-pulse">Admin</Badge>
          )}
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/users/${user.id}/edit`}>
            <Pencil className="h-4 w-4 mr-2" />
            Update user
          </Link>
        </Button>
      </div>

      <div className="grid gap-6">
        {/* User Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Details
            </CardTitle>
            <CardDescription>Basic account information</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-chromium-mist">Name</p>
                <p className="text-silica font-medium">{user.name || "—"}</p>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Email</p>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-chromium-mist" />
                  <p className="text-silica font-medium">{user.email}</p>
                  {user.emailVerified && (
                    <Badge variant="outline" className="text-neon-green border-neon-green text-xs">
                      Verified
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Role</p>
                <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                  {user.role || "user"}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Created</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-chromium-mist" />
                  <p className="text-silica font-medium">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                  </p>
                </div>
              </div>
              {user.importedAt && (
                <div>
                  <p className="text-sm text-chromium-mist">Imported</p>
                  <p className="text-silica font-medium">
                    {new Date(user.importedAt).toLocaleDateString()}
                  </p>
                </div>
              )}
              {user.importSource && (
                <div>
                  <p className="text-sm text-chromium-mist">Import Source</p>
                  <p className="text-silica font-medium">{user.importSource}</p>
                </div>
              )}
            </div>

            {user.banned && user.banReason && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                <p className="text-sm text-red-400">
                  <strong>Ban Reason:</strong> {user.banReason}
                </p>
                {user.banExpires && (
                  <p className="text-xs text-red-400 mt-1">
                    Expires: {new Date(user.banExpires).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Preferences
            </CardTitle>
            <CardDescription>
              Notifications and updates this user receives.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {emailPreferences ? (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <p className="text-sm text-silica font-medium">Research Updates</p>
                    <p className="text-sm text-chromium-mist">
                      Emails about new research papers and blog posts.
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      emailPreferences.researchUpdates
                        ? "text-neon-green border-neon-green"
                        : "text-chromium-mist border-slate-core"
                    }
                  >
                    {emailPreferences.researchUpdates ? "Enabled" : "Disabled"}
                  </Badge>
                </div>

                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <p className="text-sm text-silica font-medium">Product Updates</p>
                    <p className="text-sm text-chromium-mist">
                      Emails about new features and announcements.
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      emailPreferences.productUpdates
                        ? "text-neon-green border-neon-green"
                        : "text-chromium-mist border-slate-core"
                    }
                  >
                    {emailPreferences.productUpdates ? "Enabled" : "Disabled"}
                  </Badge>
                </div>

                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <p className="text-sm text-silica font-medium">API Usage Notifications</p>
                    <p className="text-sm text-chromium-mist">
                      Emails about API usage and quota notifications.
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      emailPreferences.apiUsage
                        ? "text-neon-green border-neon-green"
                        : "text-chromium-mist border-slate-core"
                    }
                  >
                    {emailPreferences.apiUsage ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              </div>
            ) : (
              <p className="text-chromium-mist">No email preferences saved.</p>
            )}
          </CardContent>
        </Card>

        {/* Subscriptions */}
        {userId && (
          <OwnerSubscriptions ownerId={userId} ownerName={user.name || user.email} />
        )}

        {/* Sessions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="h-5 w-5" />
                  Sessions ({sessionCount})
                </CardTitle>
                <CardDescription>Active sessions for this user</CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/users/${userId}/sessions`}>
                  View All Sessions
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-chromium-mist">
              {sessionCount === 0
                ? "No active sessions"
                : `${sessionCount} active session${sessionCount !== 1 ? "s" : ""}`}
            </p>
          </CardContent>
        </Card>

        {/* Organization Memberships */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Organization Memberships ({memberships.length})
            </CardTitle>
            <CardDescription>Organizations this user belongs to</CardDescription>
          </CardHeader>
          <CardContent>
            {memberships.length === 0 ? (
              <div className="text-center py-4 text-chromium-mist">
                <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Not a member of any organizations</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memberships.map((membership: typeof memberships[number]) => (
                    <TableRow key={membership.id}>
                      <TableCell className="font-medium text-silica">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-chromium-mist" />
                          {membership.organizationName}
                          <span className="text-xs text-chromium-mist font-mono">
                            ({membership.organizationSlug})
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{membership.role}</Badge>
                      </TableCell>
                      <TableCell className="text-chromium-mist">
                        {membership.createdAt
                          ? new Date(membership.createdAt).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/organizations/${membership.organizationSlug}`}>
                            View Org
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
